import { cn } from '@/lib/utils';
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from '@/constants';
import { FOCUSED_LAYOUT_SOURCE_IDS } from '@/constants/config';
import { TYPOGRAPHY, INTERACTION_STATES } from '@/design-system';
import { Menu, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { useOpportunityNotifications } from "@/hooks/useOpportunityNotifications";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationCenter } from "@/components/NotificationCenter";
import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";
import { PaymentFailedBanner } from "@/components/PaymentFailedBanner";
import { NewsletterPromptBanner } from "@/components/NewsletterPromptBanner";
import { HeaderSearch } from "@/components/HeaderSearch";

import { useState, useCallback } from "react";
import { useLocalizedRoutes } from "@/hooks/useLocalizedRoutes";
import { BrandLogo } from "@/components/BrandLogo";
import { LCommon } from "@/components/i18n";
import { RESPONSIVE } from "@/design-system/helpers/responsive";
import { useSidebarOptional } from "@/components/ui/sidebar";
import { NavLink } from "@/components/NavLink";
import { useGuestExitPreventionOptional } from "@/contexts/GuestExitPreventionContext";
import { useRouteEntitySafe } from "@/contexts/RouteEntityContext";
import { hasStoredSession } from "@/utils/authRedirect";



const Header = () => {
  const { user, signOut, loading, authInitialized } = useAuth();
  // A new tab/window paints before Supabase confirms the stored session.
  // Treat "not confirmed yet + stored session present" as signed in so the
  // header never flashes Sign In / Sign Up to an already-authenticated user.
  const showAuthed = user ? true : (!authInitialized || loading) && hasStoredSession();
  const { unreadCount } = useOpportunityNotifications();
  const { unreadCount: notificationCount } = useNotifications();
  const { getPath } = useLocalizedRoutes();
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarContext = useSidebarOptional();
  const setOpenMobile = sidebarContext?.setOpenMobile ?? (() => {});
  const guestExitPrevention = useGuestExitPreventionOptional();

  const isSidebarRoute = location.pathname.startsWith('/chat') || 
    location.pathname.startsWith('/account') || 
    location.pathname.startsWith('/admin');
  const routeEntity = useRouteEntitySafe();

  const isBillingFlow =
    [ROUTES.PLANS, ROUTES.CHECKOUT, ROUTES.CHECKOUT_CONFIRMATION]
      .some(route => location.pathname === route) ||
    (routeEntity?.currentEntity?.type === 'route' &&
     FOCUSED_LAYOUT_SOURCE_IDS.has(routeEntity.currentEntity.sourceId ?? ''));
  
  // Helper to handle button navigation with guest exit prevention
  const handleNavigate = useCallback((path: string) => {
    if (guestExitPrevention?.requestNavigation(path)) {
      return; // Modal was shown, don't navigate
    }
    navigate(path);
  }, [guestExitPrevention, navigate]);

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background" style={{ viewTransitionName: 'site-header' }}>
      <div className="container flex h-16 items-center relative">
        {/* Logo */}
        <BrandLogo size="md" />

        {/* Navigation - hidden on billing flow */}
        {!isBillingFlow && (
        <nav className="hidden md:flex items-center gap-2 ml-8">
          <Button
            variant="ghost"
            size="sm"
            className={cn(TYPOGRAPHY.cardDescription, INTERACTION_STATES.navButton.hover)}
            asChild
          >
            <NavLink to={getPath('experts')}><LCommon id="nav.experts" defaultValue='Experts' /></NavLink>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(TYPOGRAPHY.cardDescription, INTERACTION_STATES.navButton.hover)}
            asChild
          >
            <NavLink to={getPath('explore')}><LCommon id="nav.explore" defaultValue='Explore' /></NavLink>
          </Button>
        </nav>
        )}

        {/* Right Actions - hidden on billing flow */}
        {!isBillingFlow && (
        <div className={`flex items-center ${RESPONSIVE.gapResponsiveMdLg} ml-auto`}>
          <HeaderSearch />
          {showAuthed ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className={cn("relative h-10 w-10", INTERACTION_STATES.navButton.hover)}
                onClick={() => setNotificationCenterOpen(true)}
              >
                <BellRing className="h-5 w-5" />
                {notificationCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="h-5 min-w-5 flex items-center justify-center px-1 text-[10px] font-normal absolute -top-1 -right-1"
                  >
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </Badge>
                )}
              </Button>


              {/* Avatar - Direct link to Overview (desktop only, mobile uses bottom nav) */}
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full hidden md:flex items-center justify-center hover:border hover:border-border"
                onClick={() => handleNavigate(ROUTES.ACCOUNT_OVERVIEW)}
              >
                <UserAvatar size="sm" className="h-8 w-8" />
              </Button>
            </>
          ) : (
            <>
              {/* Join as Expert */}
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  TYPOGRAPHY.cardDescription,
                  'hover:text-foreground hover:bg-muted hidden md:flex'
                )}
                onClick={() => handleNavigate(ROUTES.AUTH_SIGNUP)}
              >
                <LCommon id="nav.join_expert" defaultValue='Join as Expert' />
              </Button>

              {/* Sign In - subtle ghost button */}
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  TYPOGRAPHY.cardDescription,
                  'hover:text-foreground hover:bg-muted hidden md:flex'
                )}
                onClick={() => handleNavigate(ROUTES.AUTH)}
              >
                <LCommon id="nav.log_in" defaultValue='Sign In' />
              </Button>

              {/* Sign Up - primary button (desktop) */}
              <Button 
                variant="default" 
                size="sm" 
                className="text-sm hidden md:flex"
                onClick={() => handleNavigate(ROUTES.AUTH_SIGNUP)}
              >
                <LCommon id="nav.sign_up" defaultValue='Sign Up' />
              </Button>

              {/* Sign Up - compact button (mobile only) */}
              <Button 
                variant="default" 
                size="sm" 
                className="text-sm md:hidden"
                onClick={() => handleNavigate(ROUTES.AUTH_SIGNUP)}
              >
                <LCommon id="nav.sign_up" defaultValue='Sign Up' />
              </Button>
            </>
          )}

          {/* Mobile Menu Button - always visible on mobile */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden relative hover:text-foreground hover:bg-muted"
            onClick={() => {
              if (isSidebarRoute) {
                setOpenMobile(true);
              } else {
                setMobileNavOpen(true);
              }
            }}
          >
            <Menu className="h-5 w-5" />
            {showAuthed && (unreadCount > 0 || notificationCount > 0) && (
              <Badge 
                variant="destructive" 
                className="h-5 min-w-5 flex items-center justify-center px-1 text-[10px] font-normal absolute -top-1 -right-1"
              >
                {(unreadCount + notificationCount) > 9 ? '9+' : (unreadCount + notificationCount)}
              </Badge>
            )}
          </Button>
        </div>
        )}
      </div>

      {/* Notification Center */}
      <NotificationCenter
        open={notificationCenterOpen}
        onOpenChange={setNotificationCenterOpen}
      />
      </header>
      {/* Email Verification Banner */}
      <EmailVerificationBanner />
      {/* Payment Failed Banner */}
      <PaymentFailedBanner />
      {/* Newsletter prompt (hidden while unverified) */}
      <NewsletterPromptBanner />



      {/* Mobile Nav Sheet for public pages */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="right" className="w-[280px] p-0">
          <SheetHeader className="p-6 pb-2 border-b border-border">
            <SheetTitle className="text-left">
              <BrandLogo size="md" />
            </SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col p-4 gap-1">
            <Button variant="ghost" className="justify-start" asChild>
              <NavLink to={getPath('experts')} onClick={() => setMobileNavOpen(false)}>
                <LCommon id="nav.experts" defaultValue="Experts" />
              </NavLink>
            </Button>
            <Button variant="ghost" className="justify-start" asChild>
              <NavLink to={getPath('explore')} onClick={() => setMobileNavOpen(false)}>
                <LCommon id="nav.explore" defaultValue="Explore" />
              </NavLink>
            </Button>
            <div className="my-2 border-t border-border" />
            {!showAuthed && (
              <>
                <Button variant="ghost" className="justify-start" onClick={() => { setMobileNavOpen(false); handleNavigate(ROUTES.AUTH); }}>
                  <LCommon id="nav.log_in" defaultValue="Sign In" />
                </Button>
                <Button variant="ghost" className="justify-start" onClick={() => { setMobileNavOpen(false); handleNavigate(ROUTES.AUTH_SIGNUP); }}>
                  <LCommon id="nav.join_expert" defaultValue="Join as Expert" />
                </Button>
                <Button variant="default" className="mt-2" onClick={() => { setMobileNavOpen(false); handleNavigate(ROUTES.AUTH_SIGNUP); }}>
                  <LCommon id="nav.sign_up" defaultValue="Sign Up" />
                </Button>
              </>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Header;
